/**
 * Renders the community's SVG site plan as a masked overlay on top of Maps SDK.
 *
 * ## What this plugin does
 * Fetches site plan data for the current community (via a Redux epic triggered when the
 * Maps SDK library starts loading), then positions the SVG as a geographically-bound
 * OverlayView on the map using the `mapBlobUrl` + `bounds` resource shape.
 * Routes lot polygon interactions (hover, click) to the handler layer.
 *
 * ## What this plugin does NOT do
 * - It does not render lot status colors, selected/hover highlight states, or map chips
 *   (that belongs to the lot polygon coloring system, a separate concern).
 * - It does not manage the right-side panel (floor plan cards, QMI cards, etc.).
 * - It does not handle filter logic.
 * - It does not load Maps SDK itself (`mapLibrary.plugin` owns that).
 * - It does not decide which layers are visible — that business rule lives in
 *   `services/svgLayerVisibility.js` and the DOM work in `components/svgLayerVisibility.dom.js`.
 *
 * ## Where to look instead
 * - Lot color / selection state → search for `lotPolygon` or `lotStatus` in this feature
 * - Right-side panel / cards    → the Community Explorer panel plugin
 * - Map chip filters            → the map controls plugin
 * - Map loading lifecycle       → `plugins/map.plugin.js`
 * - SVG overlay mechanics       → `libraries/mapLibrary/svgComposedOverlay.js`
 *
 */
import { take } from 'rxjs/operators';
import createFetchCommunitySiteMapEpic from '../redux/fetchCommunitySiteMapEpic';
import siteMapResource$ from '../redux/observables';
import { createLotPolygonInteractionHandlers } from '../handlers/lotPolygonInteractionHandlers';
import { SVG_LAYER_VISIBILITY } from '../constants';
import computeVisibleLayers from '../services/svgLayerVisibility';
import createSvgLayerVisibilityComponent from '../components/svgLayerVisibility.dom';

/**
 * Plugin that renders the community's SVG site plan as a Maps SDK overlay.
 *
 * Lifecycle:
 *  - init:  Registers the fetch epic so data loading starts in parallel with the map library.
 *  - start: Subscribes to `siteMapResource$` and the Redux store. The overlay is created only
 *           once both the resource has arrived AND the map is ready (`tryCreateOverlay`).
 *           The dual-subscription pattern is needed because the epic fires on `mapLibraryFetch`
 *           (script injected) while the map instance itself is created later on `mapLibraryLoaded`.
 *  - stop:  Detaches the overlay and cancels all subscriptions and map event listeners.
 *
 * @returns {import('./app').Plugin}
 */
export default function createRenderCommunitySiteMapPlugin() {
  const state = {
    started:              false,
    overlay:              null,
    mapFacade:            null,
    pendingResource:      null,
    resourceSubscription: null,
    mapReadySubscription: null,
    zoomUnsubscribe:      null,
  };

  return {
    name: 'render-community-site-map',

    async init(context) {
      const { communityId } = context.state.community ?? {};
      const epic = createFetchCommunitySiteMapEpic({
        communityId,
        dataActions: context.services.dataActions,
        mapLibraryActions: context.services.mapLibraryActions,
      });
      context.services.epic$.next(epic);
    },

    async start(context) {
      if (state.started) {
        return;
      }

      const mapFacade = context.services.mapFacade;
      if (!mapFacade) {
        return;
      }

      state.mapFacade = mapFacade;
      state.started = true;

      const handlers = createLotPolygonInteractionHandlers();

      function tryCreateOverlay() {
        if (state.overlay || !state.pendingResource) {
          return;
        }

        const overlay = mapFacade.createSvgComposedOverlay(state.pendingResource, {
          onLotClick:  ({ lotId }) => handlers.onLotClick({ lotId }),
          onLotHover:  ({ lotId }) => handlers.onLotHover({ lotId }),
          onLotEnter:  (args) => handlers.onLotEnter(args),
          onLotLeave:  (args) => handlers.onLotLeave(args),
        });

        if (!overlay) {
          return;
        }

        state.overlay = overlay;
        overlay.attach();

        overlay.onReady((svgElement) => {
          if (!svgElement) {
            return;
          }
          
          handlers.onSvgReady(svgElement);
          const visibilityComponent = createSvgLayerVisibilityComponent(svgElement);

          function applyZoom() {
            const { zoom } = mapFacade.getCameraSnapshot();
            visibilityComponent.setVisibleLayerTypes(computeVisibleLayers(zoom, SVG_LAYER_VISIBILITY));
          }

          applyZoom();

          state.zoomUnsubscribe = mapFacade.onMapEvent('idle', applyZoom);
        });
      }

      // Subscribe to resource observable; store for tryCreateOverlay
      state.resourceSubscription = siteMapResource$.subscribe((resource) => {
        state.pendingResource = resource;
        tryCreateOverlay();
      });

      // Wait for the map library to have loaded; at that point map.plugin has already called
      // createMap (it subscribes to the same mapLibraryLoaded$ and starts earlier in plugin order),
      // so createSvgComposedOverlay will find adapterState.map set.
      state.mapReadySubscription = context.services.mapLibraryLoaded$
        .pipe(take(1))
        .subscribe(() => {
          tryCreateOverlay();
        });
    },

    async stop() {
      state.zoomUnsubscribe?.();
      state.zoomUnsubscribe = null;

      state.resourceSubscription?.unsubscribe?.();
      state.resourceSubscription = null;

      state.mapReadySubscription?.unsubscribe?.();
      state.mapReadySubscription = null;

      state.overlay?.detach?.();
      state.overlay = null;

      state.mapFacade       = null;
      state.pendingResource = null;
      state.started         = false;
    },
  };
}
